import { ref, computed, ComputedRef, unref } from 'vue'
import { useEventListener } from '/@/hooks/event/useEventListener'
import { screenMap, sizeEnum, screenEnum } from '/@/enums/breakpointEnum'

let globalScreenRef: ComputedRef<sizeEnum | undefined>
let globalWidthRef: ComputedRef<number>
let globalRealWidthRef: ComputedRef<number>

export interface CreateCallbackParams {
  screen: ComputedRef<sizeEnum | undefined>
  width: ComputedRef<number>
  realWidth: ComputedRef<number>
  screenEnum: typeof screenEnum
  screenMap: Map<sizeEnum, number>
  sizeEnum: typeof sizeEnum
}

export function useBreakpoint() {
  return {
    screenRef: computed(() => unref(globalScreenRef)),
    widthRef: globalWidthRef,
    screenEnum,
    realWidthRef: globalRealWidthRef,
  }
}

// 计算初始断点值
function getInitialScreenSize(): sizeEnum {
  const width = window.innerWidth
  const sm = screenMap.get(sizeEnum.SM)!
  const md = screenMap.get(sizeEnum.MD)!
  const lg = screenMap.get(sizeEnum.LG)!
  const xl = screenMap.get(sizeEnum.XL)!
  const xxl = screenMap.get(sizeEnum.XXL)!

  if (width < sm) return sizeEnum.XS
  if (width < md) return sizeEnum.SM
  if (width < lg) return sizeEnum.MD
  if (width < xl) return sizeEnum.LG
  if (width < xxl) return sizeEnum.XL
  return sizeEnum.XXL
}

// Just call it once
export function createBreakpointListen(fn?: (opt: CreateCallbackParams) => void) {
  const screenRef = ref<sizeEnum>(getInitialScreenSize())
  const realWidthRef = ref(window.innerWidth)

  function getWindowWidth() {
    const width = window.innerWidth
    const sm = screenMap.get(sizeEnum.SM)!
    const md = screenMap.get(sizeEnum.MD)!
    const lg = screenMap.get(sizeEnum.LG)!
    const xl = screenMap.get(sizeEnum.XL)!
    const xxl = screenMap.get(sizeEnum.XXL)!

    // 断点值表示该尺寸的起始值（min-width），与 CSS 媒体查询保持一致
    if (width < sm) {
      screenRef.value = sizeEnum.XS // 0 - 1439
    } else if (width < md) {
      screenRef.value = sizeEnum.SM // 1440 - 1599
    } else if (width < lg) {
      screenRef.value = sizeEnum.MD // 1600 - 1919
    } else if (width < xl) {
      screenRef.value = sizeEnum.LG // 1920 - 2559
    } else if (width < xxl) {
      screenRef.value = sizeEnum.XL // 2560 - 3839
    } else {
      screenRef.value = sizeEnum.XXL // >= 3840
    }
    realWidthRef.value = width
  }

  useEventListener({
    el: window,
    name: 'resize',

    listener: () => {
      getWindowWidth()
      resizeFn()
    },
    // wait: 100,
  })

  getWindowWidth()
  globalScreenRef = computed(() => unref(screenRef))
  globalWidthRef = computed((): number => screenMap.get(unref(screenRef)!)!)
  globalRealWidthRef = computed((): number => unref(realWidthRef))

  function resizeFn() {
    fn?.({
      screen: globalScreenRef,
      width: globalWidthRef,
      realWidth: globalRealWidthRef,
      screenEnum,
      screenMap,
      sizeEnum,
    })
  }

  resizeFn()
  return {
    screenRef: globalScreenRef,
    screenEnum,
    widthRef: globalWidthRef,
    realWidthRef: globalRealWidthRef,
  }
}
